import * as yup from "yup";
import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    DatePicker,
    FormCheckbox,
    Input,
    Select,
} from "@jhool-io/fe-components";
import { IAddSessionContent } from "../../types/clients.types";
import useToast from "../../../../hooks/useToast";
import { useAddClientSession } from "../../hooks/clients.mutations";
import { useFetchActiveProviders } from "../../../providers/hooks/providers.queries";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import {
    formatZonedTimeToUtc,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { SessionServiceType } from "../../../notes/types/notes.types";
import { RecurrenceRule } from "../../../../utils/types";
import { SessionDeliveryOption } from "../../../../utils/types/session";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface AddClientSessionProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
}

const schema = yup.object({
    provider_id: yup.string().required("Provider is required"),
    service: yup.string().required("Service Type is required"),
    recurrence_rule: yup.string().required("Recurrence is required"),
    delivery_option: yup.string(),
    is_supervisor_intake_session: yup.boolean(),
    start_date: yup.string().required("First session date is required"),
    session_start_time: yup.string().required("Session start time is required"),
    session_end_time: yup
        .string()
        .required("Session end time is required")
        .test({
            name: "is-greater",
            message: "Session end time should be later than the start time",
            test: (
                value: string | undefined,
                context: {
                    parent: {
                        session_start_time: string;
                    };
                }
            ) => {
                const { session_start_time: sessionStartTime } = context.parent;
                if (!sessionStartTime || !value) {
                    return true;
                }
                const start = new Date(`1970-01-01T${sessionStartTime}:00`);
                const end = new Date(`1970-01-01T${value}:00`);
                return end > start;
            },
        }),
});

export default function AddClientSession({
    onFormSubmit,
}: AddClientSessionProps) {
    const [providerName, setProviderName] = React.useState("");
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [searchParams] = useSearchParams();

    const params = useParams();
    const navigate = useNavigate();

    const { clientId } = params;
    const clientIdFromForm = searchParams.get("new_client");

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IAddSessionContent>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const addSession = useAddClientSession(
        (clientId ?? clientIdFromForm) || ""
    );

    const activeProviders = useFetchActiveProviders(providerName);

    const providersForSelect = activeProviders.data?.data.map((provider) => ({
        label: <div className="capitalize">{provider.provider_name}</div>,
        value: provider.provider_id,
    }));

    const getServiceTypeSelectOptions = Object.values(SessionServiceType).map(
        (serviceType) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(serviceType)}
                </span>
            ),
            value: serviceType,
        })
    );

    const getRecurrenceRuleSelectOptions = Object.values(RecurrenceRule).map(
        (recurrenceRule) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(recurrenceRule)}
                </span>
            ),
            value: recurrenceRule,
        })
    );

    const getDeliverySelectOptions = Object.values(SessionDeliveryOption).map(
        (deliveryOption) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(deliveryOption)}
                </span>
            ),
            value: deliveryOption,
        })
    );

    const onSubmit = (data: IAddSessionContent) => {
        const formattedStartTime = formatZonedTimeToUtc(
            `${new Date(data.start_date).toISOString().split("T")[0]}T${
                data.session_start_time.split(":")[0]
            }:${data.session_start_time.split(":")[1]}:00.000`
        );

        const formattedEndTime = formatZonedTimeToUtc(
            `${new Date(data.start_date).toISOString().split("T")[0]}T${
                data.session_end_time.split(":")[0]
            }:${data.session_end_time.split(":")[1]}:00.000`
        );

        const dataToSend = {
            sessions: [
                {
                    ...data,
                    session_start_time: formattedStartTime,
                    session_end_time: formattedEndTime,
                },
            ],
        };

        addSession.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "provider-history"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Session added successfully",
                });
                if (clientIdFromForm) {
                    navigate(`/clients/${clientIdFromForm}`);
                }
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add session at this time.",
                });
            },
        });
    };

    return (
        <form id="add-session" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Controller
                    name="provider_id"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Provider"
                            options={
                                activeProviders.data ||
                                !activeProviders.error ||
                                !activeProviders.isLoading
                                    ? providersForSelect
                                    : []
                            }
                            isDisabled={Boolean(activeProviders.error)}
                            onInputChange={(value) => setProviderName(value)}
                            filterOption={() => true}
                            isSearchable
                            placeholder={
                                (!activeProviders.data &&
                                    activeProviders.isLoading &&
                                    "Loading...") ||
                                (activeProviders.error &&
                                    !activeProviders.isLoading &&
                                    "Error loading providers") ||
                                (activeProviders.data &&
                                    !activeProviders.isLoading &&
                                    "Provider")
                            }
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.provider_id}
                            errorText={errors.provider_id?.message}
                            // eslint-disable-next-line react/no-unstable-nested-components
                            noOptionsMessage={() => {
                                if (activeProviders.isLoading) {
                                    return <Skeleton width="100%" />;
                                }
                                return "No options";
                            }}
                            isLongListInDialog
                            isRequired
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="service"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Service type"
                            placeholder="Service type"
                            isSearchable
                            options={getServiceTypeSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.service}
                            errorText={errors.service?.message}
                            isRequired
                        />
                    )}
                />
            </div>

            <div className="fg fg-space-between two flex">
                <Controller
                    name="delivery_option"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Delivery Option"
                            placeholder="Delivery Option"
                            isSearchable
                            options={getDeliverySelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.delivery_option}
                            errorText={errors.delivery_option?.message}
                            isRequired
                        />
                    )}
                />
                <Controller
                    name="recurrence_rule"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Recurrence"
                            placeholder="Recurrence"
                            isSearchable
                            options={getRecurrenceRuleSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.recurrence_rule}
                            errorText={errors.recurrence_rule?.message}
                            isRequired
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="First Session Date"
                            onChange={(date) => {
                                field.onChange(date);
                                setStartDate(date);
                            }}
                            selected={startDate}
                            hasError={!!errors.start_date}
                            errorText={
                                errors.start_date?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.start_date?.message
                            }
                            isRequired
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    {...register(`session_start_time`)}
                    label="Start time"
                    placeholder="Start time"
                    hasError={!!errors.session_start_time}
                    errorText={errors.session_start_time?.message}
                    type="time"
                    isRequired
                />

                <Input
                    {...register(`session_end_time`)}
                    label="End time"
                    placeholder="End time"
                    hasError={!!errors.session_end_time}
                    errorText={errors.session_end_time?.message}
                    type="time"
                    isRequired
                />
            </div>
            <div className="fg">
                <FormCheckbox
                    {...register("is_supervisor_intake_session")}
                    label="Supervisor intake session"
                />
            </div>
        </form>
    );
}
